import { useCallback, useEffect, useState } from 'react';
import type { CommentAnchor } from '@domain/schemas/comment.schema';

import { rangeToAnchor } from '../utils/anchor-dom';

export interface InlineSelection {
  blockId: string;
  anchor: CommentAnchor;
  rect: { top: number; left: number; bottom: number; width: number };
}

const BLOCK_CONTENT_SELECTOR = '[data-block-content]';
const KEYBOARD_SELECTION_DELAY_MS = 250;

const closestBlockContent = (node: Node | null): HTMLElement | null => {
  const element = node instanceof Element ? node : (node?.parentElement ?? null);
  return element?.closest<HTMLElement>(BLOCK_CONTENT_SELECTOR) ?? null;
};

// jsdom has no layout; the pill only needs a rect in the browser.
const rectOf = (range: Range) => {
  const rect = typeof range.getBoundingClientRect === 'function' ? range.getBoundingClientRect() : undefined;
  return { top: rect?.top ?? 0, left: rect?.left ?? 0, bottom: rect?.bottom ?? 0, width: rect?.width ?? 0 };
};

/** The current browser selection as a comment anchor, when it sits inside one block's content. */
export const readInlineSelection = (): InlineSelection | null => {
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0 || selection.isCollapsed) {
    return null;
  }
  const range = selection.getRangeAt(0);
  const root = closestBlockContent(range.startContainer);
  if (!root || root !== closestBlockContent(range.endContainer)) {
    return null;
  }
  const local = rangeToAnchor(range, root);
  if (!local) {
    return null;
  }
  const offset = Number(root.getAttribute('data-block-content-offset') ?? 0);
  return {
    blockId: root.getAttribute('data-block-content') ?? '',
    anchor: { ...local, start: local.start + offset, end: local.end + offset },
    rect: rectOf(range),
  };
};

export const useInlineCommentSelection = (enabled: boolean) => {
  const [selection, setSelection] = useState<InlineSelection | null>(null);

  useEffect(() => {
    if (!enabled) {
      setSelection(null);
      return undefined;
    }

    let timer: ReturnType<typeof setTimeout> | undefined;
    const read = (delay: number) => {
      clearTimeout(timer);
      timer = setTimeout(() => setSelection(readInlineSelection()), delay);
    };
    // The selection settles after mouseup; keyboard selections arrive only through selectionchange.
    const onMouseUp = () => read(0);
    const onSelectionChange = () => {
      if (window.getSelection()?.isCollapsed !== false) {
        clearTimeout(timer);
        setSelection(null);
        return;
      }
      read(KEYBOARD_SELECTION_DELAY_MS);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setSelection(null);
      }
    };
    const onScroll = () => setSelection(null);

    document.addEventListener('mouseup', onMouseUp);
    document.addEventListener('selectionchange', onSelectionChange);
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('scroll', onScroll, true);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('mouseup', onMouseUp);
      document.removeEventListener('selectionchange', onSelectionChange);
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('scroll', onScroll, true);
    };
  }, [enabled]);

  const dismiss = useCallback(() => {
    setSelection(null);
    window.getSelection()?.removeAllRanges();
  }, []);

  return { selection, dismiss };
};
